import { useEscape } from '../lib/useEscape.js';

interface Props {
  onClose: () => void;
}

/** Guide de lecture des trois bandes et des chiffres qui en sont tirés. */
const ROWS = [
  {
    heading: 'Poids',
    color: 'var(--ink3)',
    body: "Le trait fin est la mesure brute, le trait épais la moyenne sur sept jours. Ne suivez que le trait épais : d’un jour à l’autre, le poids varie de l’ordre de 0,5 % du corps — eau, sel, glycogène et transit — soit 0,4 kg à 80 kg, parfois le double. Une coupure dans la courbe signifie une absence de mesure ; rien n’est interpolé.",
  },
  {
    heading: 'Pas',
    color: 'var(--s)',
    body: "Les barres sont les pas de la veille, la ligne pointillée leur moyenne sur la période affichée. Les barres claires sont les autres jours, la barre pleine le jour survolé ou sélectionné. Une semaine sous la moyenne explique souvent une stagnation sans aucun écart alimentaire — comme elle explique une prise plus rapide que prévu à apports constants.",
  },
  {
    heading: 'Kcal',
    color: 'var(--kt)',
    body: "Le pointillé est votre maintien, le trait plein la cible de l’objectif choisi. Visez des barres qui oscillent autour du trait plein, pas qui l’atteignent en moyenne par compensation. Un seul jour d’écart ne compte pas : c’est la surface cumulée sur une à deux semaines qui déplace la courbe du poids.",
  },
  {
    heading: 'D’où vient le maintien',
    color: 'var(--ink3)',
    body: "Il n’est pas calculé par une formule de morphologie, mais déduit de vos propres saisies : apport moyen des quatre dernières semaines, corrigé de la variation de poids sur la même période (7700 kcal par kilo). L’intérêt est qu’il absorbe vos habitudes de comptage. Si vous sous-estimez vos portions de 20 % — ce qui est la règle et non l’exception dans la littérature —, le maintien affiché est sous-estimé d’autant, et l’écart conseillé reste juste dans votre unité de mesure. Tant que quatorze jours d’apports ne sont pas saisis, un modèle générique prend le relais : il est nettement moins fiable.",
  },
  {
    heading: 'Pourquoi une marge sur la tendance',
    color: 'var(--ink3)',
    body: "La pente est une régression linéaire, et une régression a une incertitude. Avec le bruit quotidien de la pesée, cinq jours donnent une marge supérieure à ±1 kg/semaine : le chiffre affiché serait alors décoratif. Quatorze jours la ramènent vers ±0,3, vingt-huit vers ±0,2. S’y ajoute une correction souvent oubliée : une rétention d’eau dure plusieurs jours, si bien que deux écarts consécutifs à la tendance se ressemblent. Ephemeris mesure cette persistance et en déduit combien de pesées valent réellement des mesures indépendantes — avec une corrélation de 0,4, vingt-huit pesées n’en valent que douze, et la marge s’élargit de moitié. C’est pourquoi la carte annonce parfois « trop incertain pour trancher » plutôt que de colorer une pente que les données ne soutiennent pas. Pesez-vous tous les jours : chaque jour manquant élargit encore la marge.",
  },
  {
    heading: 'Lire les trois ensemble',
    color: 'var(--ink3)',
    body: "Posez le doigt ou la souris sur un graphe et faites glisser : le curseur est partagé par les trois bandes, et le bandeau de lecture affiche les valeurs du même jour. Il y reste une fois le doigt levé, et les flèches le déplacent d’un jour — plus sûr que de viser une barre large de trois pixels. Un mouvement du poids précédé de deux barres kcal éloignées du trait de cible et de barres de pas basses est une explication cohérente. Le même mouvement sans écart visible est presque toujours de la rétention d’eau.",
  },
  {
    heading: 'Filtrer',
    color: 'var(--ink3)',
    body: "30 jours pour juger une décision récente, 90 jours pour une tendance de fond, Tout pour l’historique complet. « Éditer », dans le bandeau de lecture, ouvre la journée retenue pour corriger une saisie aberrante.",
  },
];

export function InfoModal({ onClose }: Props) {
  useEscape(onClose);

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label="Guide de lecture"
      onClick={onClose}
    >
      {/* Le clic sur le fond ferme ; celui sur la boîte ne doit pas remonter. */}
      <div className="modal__box" onClick={(event) => event.stopPropagation()}>
        <header className="modal__head">
          <div>
            <div className="eyebrow">Guide de lecture</div>
            <h2 className="modal__title">Évolution comparée du poids, des pas et des calories</h2>
          </div>
          <button type="button" className="btn--icon" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </header>

        <p className="modal__lead">
          Les trois bandes partagent le même axe de temps. C’est l’alignement vertical qui porte
          l’information : on descend du poids vers les pas et les calories du même jour.
        </p>

        <div className="modal__rows">
          {ROWS.map((row) => (
            <div key={row.heading}>
              <h3 style={{ color: row.color }}>{row.heading}</h3>
              <p>{row.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
